'use client'
import React, {useEffect, useState} from 'react'
import {
  Navbar,
  NavbarContent,
  NavbarItem,
  NavbarMenu,
  NavbarMenuToggle,
  NavbarBrand,
} from '@heroui/react'
import Link from 'next/link'
import Image from 'next/image'
import {usePathname} from 'next/navigation'
import {useSession} from 'next-auth/react'

import Box from '@/components/UI/Box/Box'
import BeConsultant from '@/components/NavigationBar/BeConsultant'
import {CollapseMenuContent} from '@/components/NavigationBar/constants'
import NavSearch from '@/components/NavigationBar/NavSearch'
import NavCustomer from '@/components/NavigationBar/NavCustomer'
import NavMegaMenu from '@/components/NavigationBar/NavMegaMenu'
import NavMessages from '@/components/NavigationBar/NavMessages'
import Icon from '@/components/UI/Icon/Icon'

import {advisorApprovalStatusMap} from '@/src/shared/constants/statusMaps'
import {CustomerSummary} from '@/src/services/UserService/types'
import {getCustomerSummary} from '@/src/services/UserService/UserService'

const NavigationBar = () => {
  const [menuOpen, setMenuOpen] = useState(false)
  const pathname = usePathname()
  const {data: session} = useSession()
  const [customerSummary, setCustomerSummary] =
    useState<CustomerSummary | null>(null)

  useEffect(() => {
    setMenuOpen(false)
    if (session?.userId) {
      getCustomerSummary(session?.userId).then(customerSummary => {
        setCustomerSummary(customerSummary)
      })
    }
  }, [pathname, session])

  return (
    <Navbar
      position="static"
      height={160}
      maxWidth="xl"
      isMenuOpen={menuOpen}
      onMenuOpenChange={setMenuOpen}
      classNames={{wrapper: 'flex justify-start flex-col pt-6 gap-0'}}
      className="border-b-1 border-gray-200 flex flex-col shadow-[0_0_30px_0_rgba(0,0,0,0.1)]">
      <Box className="w-full items-center flex-col h-1/2 lg:h-full">
        <Box className="w-full items-center h-1/2">
          <NavbarContent
            aria-label={menuOpen ? 'Close Menu' : 'Open Menu'}
            className="block lg:hidden min-w-10">
            <NavbarItem aria-label="Toggle menu" className="h-full w-full">
              <NavbarMenuToggle
                icon={
                  menuOpen ? (
                    <Icon name="cross" size={20} className="fill-gray-400" />
                  ) : (
                    <Icon
                      name="mobileMenu"
                      size={20}
                      className="fill-gray-400"
                    />
                  )
                }
              />
            </NavbarItem>
          </NavbarContent>
          <NavbarContent className="w-full h-1/2 gap-8 !basis-full">
            <NavbarItem className="basis-2/12">
              <NavbarBrand className="basis-2/12">
                <Link
                  href="/"
                  className="lg:w-[300px] lg:h-[35px] w-[170px] h-[25px] relative">
                  <Image
                    alt="logo"
                    fill
                    src="/advicemy2.svg"
                    title="Advicemy: Online Psikolog, Aile Danışmanı, Pedagog ve Diyetisyen"
                    priority
                  />
                </Link>
              </NavbarBrand>
            </NavbarItem>
            <NavbarItem className="basis-6/12 hidden lg:block">
              <NavSearch />
            </NavbarItem>
            <NavbarItem className="flex lg:basis-3/12 basis-10/12 flex-row gap-4 justify-end">
              <Box className="items-center gap-4">
                <NavCustomer customerSummary={customerSummary} />
                <NavMessages />
              </Box>
              <BeConsultant customerSummary={customerSummary} />
            </NavbarItem>
          </NavbarContent>
        </Box>
        <NavbarContent
          className="w-full h-1/2 gap-4 hidden lg:block"
          justify="center">
          <NavbarItem className="h-full w-full">
            <NavMegaMenu />
          </NavbarItem>
        </NavbarContent>
      </Box>
      <NavbarContent className="w-full h-1/2 gap-4 block lg:hidden">
        <NavbarItem className="h-fit w-full">
          <NavSearch />
        </NavbarItem>
      </NavbarContent>
      <NavbarMenu>
        <NavbarItem className="h-fit w-full">
          <CollapseMenuContent
            id={customerSummary?.customerId.toString()}
            isAdvisor={!!customerSummary?.advisorStatus}
            status={
              customerSummary?.advisorStatus as keyof typeof advisorApprovalStatusMap
            }
          />
        </NavbarItem>
      </NavbarMenu>
    </Navbar>
  )
}

export default NavigationBar
